import { useSelector } from 'react-redux';
import { getStepByName } from 'accounts/utils/flow-steps';
import { getUnitsFromProperties } from 'accounts/utils/units';
import { groupBy } from 'es-toolkit/compat';
import { FlowType, SetupDataTypes } from 'models/enums';
import { selectUser } from 'store/redux/user/selectors';

export function useSteps({
  properties,
  setupFlows,
}: {
  properties: any;
  setupFlows: any;
}) {
  const user = useSelector(selectUser);

  if (!properties || !setupFlows?.length) {
    return {
      steps: null,
      allStepsByGate: null,
    };
  }

  const units = getUnitsFromProperties(properties);
  // Each entry is a generated `FlowStepModel` spread over its
  // `STEPS_CONFIGURATION` match plus flow/user/property fields — the ad-hoc shape
  // `SetupFlowStepProvider`'s context declares as `any`.
  let steps: any[] = [];

  for (const setupFlow of setupFlows) {
    steps = steps.concat(
      setupFlow?.steps
        .filter(
          (step: any) =>
            getStepByName(step.stepName) &&
            (setupFlow.flowType !== FlowType.HomeownerSetup ||
              step.dataType !== SetupDataTypes.Home),
        )
        ?.map((step: any) => {
          const result: any = {
            ...step,
            ...getStepByName(step.stepName),
            user,
            flowType: setupFlow.flowType,
            // UserId | PropertyId | HomeId
            dataRelId: user?.userId,
            flowId: setupFlow.uniqueId,
            flowStatus: setupFlow.status,
          };

          if (step.dataType === SetupDataTypes.Property) {
            const propertyData = properties.find(
              (property: any) => property.basicInfo.id === step.dataId,
            );

            result.property = propertyData;
            result.dataRelId = propertyData?.basicInfo.propertyId;
          }

          if (step.dataType === SetupDataTypes.Home) {
            // `basicInfo` is optional on `HomeownerAccountUnit`; the extra `?.`
            // leaves a populated unit reading exactly as before and yields
            // `undefined` instead of throwing on one without it.
            const unitData = units.find(
              (unit) => unit.basicInfo?.id === step.dataId,
            );
            const propertyData = properties.find((property: any) =>
              property.units.some(
                (unit: any) => unit.basicInfo.id === unitData?.basicInfo?.id,
              ),
            );

            result.unit = unitData;
            result.property = propertyData;
            result.dataRelId = unitData?.basicInfo?.unitId;
          }

          return result;
        }),
    );
  }

  return {
    steps,
    allStepsByGate: groupBy(steps, (step) => step.gate),
  };
}
